/**
 * useBallSyncQueue — IndexedDB-backed queue for ball saves while offline.
 *
 *  - When `enqueue(payload)` is called, the ball is stored locally in IDB.
 *  - On reconnection (`online` flips true), the queue is flushed in order,
 *    POSTing each ball to /api/ballbyball/record-single-ball-delivery/.
 *  - Each successful flush removes the row from IDB.
 *  - A visible queue depth + last-error are exposed to the UI.
 *
 * Ball numbering is server-derived (the persist function reads aggregate.last_ball
 * to compute ball_number), so the queue can simply replay the original payloads
 * in the order they were created.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/axios";

const DB    = "zcd_ball_queue_v1";
const STORE = "queued_balls";

function dbOpen() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}
async function idbAll(matchId) {
  const db = await dbOpen();
  return new Promise((res) => {
    const out = [];
    const tx = db.transaction(STORE, "readonly");
    tx.objectStore(STORE).openCursor().onsuccess = (e) => {
      const c = e.target.result;
      if (!c) return res(out.sort((a, b) => a.id - b.id));
      if (!matchId || c.value.matchId === matchId) out.push(c.value);
      c.continue();
    };
  });
}
async function idbPut(row) {
  const db = await dbOpen();
  const tx = db.transaction(STORE, "readwrite");
  tx.objectStore(STORE).add(row);
  return new Promise((r) => (tx.oncomplete = r));
}
async function idbDelete(id) {
  const db = await dbOpen();
  const tx = db.transaction(STORE, "readwrite");
  tx.objectStore(STORE).delete(id);
  return new Promise((r) => (tx.oncomplete = r));
}

export function useBallSyncQueue({ matchId, onSync, onError } = {}) {
  const [online, setOnline]     = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [depth, setDepth]       = useState(0);
  const [flushing, setFlushing] = useState(false);
  const [lastError, setLastError] = useState("");
  const flushingRef = useRef(false);

  const refresh = useCallback(async () => {
    const all = await idbAll(matchId);
    setDepth(all.length);
  }, [matchId]);

  // Listen to online/offline
  useEffect(() => {
    const goOnline  = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online",  goOnline);
    window.addEventListener("offline", goOffline);
    refresh();
    return () => {
      window.removeEventListener("online",  goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [refresh]);

  const enqueue = useCallback(
    async (payload, idemKey) => {
      // A key per queued ball: if a flush's POST lands but its reply is
      // lost, the row stays queued and is sent again -- the server must
      // see it as the same ball (audit C #140). A ball queued after a
      // failed live attempt keeps that attempt's key.
      const idem_key = idemKey || globalThis.crypto?.randomUUID?.()
        || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const row = { matchId, payload, queued_at: Date.now(), idem_key };
      await idbPut(row);
      await refresh();
    },
    [matchId, refresh]
  );

  const flush = useCallback(async () => {
    if (flushingRef.current) return;
    flushingRef.current = true;
    setFlushing(true);
    setLastError("");
    try {
      const all = await idbAll(matchId);
      for (const row of all) {
        try {
          const res = await api.post(
            "/api/ballbyball/record-single-ball-delivery/",
            { match_id: row.matchId, ...row.payload },
            row.idem_key ? { headers: { "X-Idempotency-Key": row.idem_key } } : undefined,
          );
          await idbDelete(row.id);
          onSync?.(row, res?.data);
        } catch (e) {
          setLastError(e?.response?.data?.detail || e.message || "Sync failed");
          onError?.(row, e);
          break; // stop at first failure to keep ordering
        }
      }
      await refresh();
    } finally {
      flushingRef.current = false;
      setFlushing(false);
    }
  }, [matchId, onSync, onError, refresh]);

  // Auto-flush when we come back online
  useEffect(() => {
    if (online && depth > 0 && !flushingRef.current) flush();
  }, [online, depth, flush]);

  return { online, depth, flushing, lastError, enqueue, flush, refresh };
}
